import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import {
  GOVERNANCE_PROFILES,
  ALL_PROFILES,
  DEFAULT_PROFILE_ID,
} from "../lib/governanceProfiles";
import {
  fetchGovernanceProfile,
  updateGovernanceProfile,
} from "../services/businessDashApi";

const STORAGE_KEY = "az_governance_profile";

const GovernanceContext = createContext(null);

export const GovernanceProvider = ({ children }) => {
  const [profileId, setProfileIdState] = useState(() => {
    return localStorage.getItem(STORAGE_KEY) || DEFAULT_PROFILE_ID;
  });
  const [isSyncing, setIsSyncing] = useState(false);

  // On mount, try to load profile preference from backend
  useEffect(() => {
    const loadFromBackend = async () => {
      try {
        const data = await fetchGovernanceProfile();
        if (data?.profile_id && GOVERNANCE_PROFILES[data.profile_id]) {
          setProfileIdState(data.profile_id);
          localStorage.setItem(STORAGE_KEY, data.profile_id);
        }
      } catch {
        // Backend endpoint may not exist yet — fall back to localStorage value
      }
    };
    loadFromBackend();
  }, []);

  const setProfileId = useCallback(async (newId) => {
    if (!GOVERNANCE_PROFILES[newId]) return;
    setProfileIdState(newId);
    localStorage.setItem(STORAGE_KEY, newId);

    // Optimistically sync to backend
    setIsSyncing(true);
    try {
      await updateGovernanceProfile(newId);
    } catch {
      // Silently fail — localStorage value is the source of truth until backend is ready
    } finally {
      setIsSyncing(false);
    }
  }, []);

  const profile =
    GOVERNANCE_PROFILES[profileId] || GOVERNANCE_PROFILES[DEFAULT_PROFILE_ID];

  return (
    <GovernanceContext.Provider
      value={{
        profile,
        profileId,
        setProfileId,
        allProfiles: ALL_PROFILES,
        isSyncing,
      }}
    >
      {children}
    </GovernanceContext.Provider>
  );
};

export const useGovernance = () => {
  const ctx = useContext(GovernanceContext);
  if (!ctx) {
    throw new Error("useGovernance must be used within a GovernanceProvider");
  }
  return ctx;
};

export default GovernanceContext;
